import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/Components/Icon/Icon';
import { KeyboardKey } from '@standardnotes/ui-services';
function PortalImpl({ onClose, children, title, closeOnClickOutside, }) {
    const modalRef = useRef(null);
    useEffect(() => {
        if (modalRef.current !== null) {
            modalRef.current.focus();
        }
    }, []);
    useEffect(() => {
        let modalOverlayElement = null;
        const keydownHandler = (event) => {
            if (event.key === KeyboardKey.Escape) {
                onClose();
            }
        };
        const clickOutsideHandler = (event) => {
            const target = event.target;
            if (modalRef.current !== null && !modalRef.current.contains(target) && closeOnClickOutside) {
                onClose();
            }
        };
        if (modalRef.current !== null) {
            modalOverlayElement = modalRef.current.parentElement;
            if (modalOverlayElement !== null) {
                modalOverlayElement.addEventListener('click', clickOutsideHandler);
            }
        }
        window.addEventListener('keydown', keydownHandler);
        return () => {
            window.removeEventListener('keydown', keydownHandler);
            if (modalOverlayElement !== null) {
                modalOverlayElement.removeEventListener('click', clickOutsideHandler);
            }
        };
    }, [closeOnClickOutside, onClose]);
    return (_jsx("div", { className: "absolute inset-0 flex flex-col items-center justify-center bg-[rgba(0,0,0,0.65)]", role: "dialog", children: _jsxs("div", { className: "relative flex min-w-[min(80vw,_20rem)] flex-col rounded border border-border bg-default", tabIndex: -1, ref: modalRef, children: [_jsxs("div", { className: "flex items-center justify-between border-b border-border px-3.5 py-2", children: [_jsx("div", { className: "text-sm font-semibold", children: title }), _jsx("button", { tabIndex: 0, className: "ml-2 rounded p-1 font-bold hover:bg-contrast", onClick: onClose, children: _jsx(Icon, { type: "close" }) })] }), _jsx("div", { className: "px-3.5 py-3", children: children })] }) }));
}
export default function Modal({ onClose, children, title, closeOnClickOutside = true, }) {
    const [containerElement, setContainerElement] = useState();
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        var _a, _b;
        setContainerElement((_b = (_a = editor.getRootElement()) === null || _a === void 0 ? void 0 : _a.parentElement) !== null && _b !== void 0 ? _b : document.body);
    }, [editor]);
    if (!containerElement) {
        return null;
    }
    return createPortal(_jsx(PortalImpl, { onClose: onClose, title: title, closeOnClickOutside: closeOnClickOutside, children: children }), containerElement);
}
